import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sheet, SheetContent, SheetTrigger } from '../../ui/sheet';
import { Button } from '../../ui/button';
import {
  Menu,
  Home,
  Globe,
  MessageSquare,
  User,
  LayoutDashboard,
  Settings,
} from 'lucide-react';
import LanguageSelector from '../../LanguageSelector';
import MobileSearchField from './MobileSearchField';

/**
 * Authenticated mobile menu — right-side sheet with grouped nav + profile shortcuts.
 */
export default function MobileMenu({ user, filteredNavGroups, open, setOpen, isActive }) {
  const location = useLocation();
  const close = () => setOpen(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild className="lg:hidden">
        <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger" className="text-white hover:bg-white/10 hover:text-white">
          <Menu className="h-6 w-6" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-80 overflow-y-auto">
        <nav className="flex flex-col gap-1 mt-8">
          <MobileSearchField onSubmit={close} />
          {/* Language */}
          <div className="px-4 pb-4 mb-2 border-b">
            <p className="text-xs text-muted-foreground mb-2">Language</p>
            <LanguageSelector variant="default" showLabel={true} />
          </div>

          {/* Feed direct link */}
          <Link
            to="/feed"
            onClick={close}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              isActive('/feed') ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
            }`}
          >
            <Home className="w-5 h-5" />
            Feed
          </Link>

          {/* Grouped nav items */}
          {Object.entries(filteredNavGroups).map(([key, group]) => (
            <div key={key} className="mt-4">
              <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {group.label}
              </div>
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={close}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive(item.href)
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-muted'
                    }`}
                  >
                    <Icon
                      className={`w-5 h-5 ${
                        item.guestAccessible && !isActive(item.href) ? 'text-emerald-600' : ''
                      }`}
                    />
                    {item.label}
                    {item.guestAccessible && !isActive(item.href) && (
                      <Globe className="w-3 h-3 text-emerald-600 ml-auto" />
                    )}
                  </Link>
                );
              })}
            </div>
          ))}

          {/* Profile shortcuts */}
          <div className="mt-4 pt-4 border-t">
            <Link
              to={`/beta-feedback?from=${encodeURIComponent(location.pathname)}`}
              onClick={close}
              className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted"
            >
              <MessageSquare className="h-4 w-4" />
              Feedback & Updates
            </Link>
            <Link
              to={`/profile/${user.username}`}
              onClick={close}
              className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted"
            >
              <User className="w-5 h-5" />
              My Profile
            </Link>
            <Link
              to="/dashboard"
              onClick={close}
              className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted"
              data-testid="mobile-menu-dashboard"
            >
              <LayoutDashboard className="w-5 h-5" />
              My Dashboard
            </Link>
            <Link
              to="/settings"
              onClick={close}
              className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted"
            >
              <Settings className="w-5 h-5" />
              Settings
            </Link>
          </div>
        </nav>
      </SheetContent>
    </Sheet>
  );
}
